"use client";

import { PaywallSurface, trackPaywallView } from "@/lib/analytics/gtm-datalayer";
import { buildPaywallSourceProperties } from "@/lib/analytics/paywall-source";
import { useEffect, useRef } from "react";

interface PaywallViewTrackingOptions {
  enabled?: boolean;
  offerMode?: string;
  source?: string;
  surface: PaywallSurface;
  userIntentSegment?: string;
  userType?: "guest" | "authenticated";
  posthog?: {
    capture: (eventName: string, properties?: Record<string, unknown>) => unknown;
  } | null;
  posthogProperties?: Record<string, unknown>;
}

/**
 * Tracks a pricing surface once it is actually visible. Page-level paywalls can
 * opt in after their plans load; below-the-fold pricing avoids false views from
 * merely being mounted in the document.
 */
export function usePaywallViewTracking<T extends HTMLElement>({
  enabled = true,
  offerMode,
  source,
  surface,
  userIntentSegment,
  userType,
  posthog,
  posthogProperties,
}: PaywallViewTrackingOptions) {
  const elementRef = useRef<T>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasTrackedRef.current) return;

    const element = elementRef.current;
    if (!element) return;

    const trackVisiblePaywall = () => {
      if (hasTrackedRef.current) return;
      hasTrackedRef.current = true;
      const paywallSourceProperties = buildPaywallSourceProperties(source);
      const paywallProperties = {
        offerMode,
        source: paywallSourceProperties.source,
        surface,
        userIntentSegment,
        userType,
      };
      trackPaywallView(paywallProperties);
      if (posthog) {
        posthog.capture("paywall_view", {
          ...posthogProperties,
          offer_mode: offerMode,
          ...paywallSourceProperties,
          surface,
          user_intent_segment: userIntentSegment,
          user_type: userType,
        });
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      trackVisiblePaywall();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          trackVisiblePaywall();
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [
    enabled,
    offerMode,
    posthog,
    posthogProperties,
    source,
    surface,
    userIntentSegment,
    userType,
  ]);

  return elementRef;
}
